<template>
  <div
    ref="rowElement"
    class="pay-plan-row-card"
    :class="rowClasses"
    role="button"
    tabindex="0"
    :aria-label="ariaLabel"
    @click="handleRowClick"
    @keydown.enter.prevent="handleRowActivate"
    @keydown.space.prevent="handleRowActivate"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @focus="isFocused = true"
    @blur="isFocused = false"
  >
    <div class="pay-plan-row-card__summary">
      <VersionBadge v-if="!hideVersionBadge" :code="resolvedVersionCode" />
      <span
        class="pay-plan-row-card__amount"
        :class="{ 'pay-plan-row-card__struck': isVoided }"
      >
        {{ resolvedAmount }}
      </span>
      <p
        class="pay-plan-row-card__effective"
        :class="{ 'pay-plan-row-card__struck': isVoided }"
      >
        {{ effectiveText }}
        <span v-if="openEndedDashVisible" class="pay-plan-row-card__open-end">
          through
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span
                class="pay-plan-row-card__open-end-dash"
                v-bind="attrs"
                v-on="on"
              >&mdash;</span>
            </template>
            <span>Open-ended</span>
          </v-tooltip>
        </span>
      </p>
    </div>

    <div class="pay-plan-row-card__badges">
      <PayPlanLifecycleBadge
        :status="lifecycleStatus"
        :approval-status="approvalStatus"
        :label="statusLabel"
      />
      <PayPlanPeriodStateBadge
        v-if="showStateBadge"
        :state="periodState"
        :filled="stateBadgeFilled"
      />
      <slot name="badges-extra" />
    </div>

    <div class="pay-plan-row-card__actions">
      <button
        type="button"
        class="pay-plan-row-card__chatter-btn"
        :aria-label="`Open ${entityLabel.toLowerCase()} chatter`"
        @click.stop="emit('open-chatter', $event)"
      >
        <ChatterIcon :is_white="chatterIsWhite" :unread="unread" />
      </button>
      <DropDownMenu
        v-if="showMenu"
        class="pay-plan-row-card__menu"
        :dropdown-items="resolvedMenuItems"
        :is-open="false"
        :width="200"
        tip-side="right"
        :translate-y-offset="8"
        @onClick="onMenuAction"
      >
        <template #activator>
          <button
            type="button"
            class="pay-plan-row-card__menu-btn"
            :aria-label="`${entityLabel} actions`"
          >
            <svg
              class="pay-plan-row-card__menu-icon"
              width="4"
              height="18"
              viewBox="0 0 4 18"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="2" cy="2" r="1" stroke="currentColor" stroke-width="2" />
              <circle cx="2" cy="9" r="1" stroke="currentColor" stroke-width="2" />
              <circle cx="2" cy="16" r="1" stroke="currentColor" stroke-width="2" />
            </svg>
          </button>
        </template>
      </DropDownMenu>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";

import {
  buildPayPlanRowMenuItems,
  formatPayPlanAmount,
  formatPayPlanEffectiveText,
  formatPayPlanVersionCode,
  normalizePayPlanLifecycleStatus,
  normalizePayPlanPeriodState,
  payPlanRowIsVoided,
  payPlanRowPeriodBarState,
  payPlanRowShowsMenu,
  payPlanRowShowsStateBadge,
  payPlanRowSurfaceClass,
  payPlanRowUsesFilledStateBadge,
  payPlanRowIsCompact,
  resolvePayPlanPeriodState,
} from "./payPlanRowCardUtils";

const props = defineProps({
  status: {
    type: String,
    required: true,
  },
  state: {
    type: String,
    default: "",
  },
  effectiveFrom: {
    type: [String, Date],
    required: true,
  },
  effectiveTo: {
    type: [String, Date],
    default: null,
  },
  openEnded: {
    type: Boolean,
    default: false,
  },
  amount: {
    type: [Number, String],
    default: null,
  },
  amountFormatted: {
    type: String,
    default: "",
  },
  currencyCode: {
    type: String,
    default: "USD",
  },
  planNumber: {
    type: [Number, String],
    default: null,
  },
  versionNumber: {
    type: [Number, String],
    default: null,
  },
  versionCode: {
    type: String,
    default: "",
  },
  targetsExistingPlan: {
    type: Boolean,
    default: false,
  },
  referenceDate: {
    type: [String, Date],
    default: () => new Date(),
  },
  selected: {
    type: Boolean,
    default: false,
  },
  /** Storybook-only: force hover / focus ring without real pointer state. */
  interaction: {
    type: String,
    default: "default",
    validator(value) {
      return ["default", "hover", "selected", "focus"].includes(value);
    },
  },
  unread: {
    type: Number,
    default: 0,
  },
  menuItems: {
    type: Array,
    default: null,
  },
  approvalStatus: {
    type: String,
    default: "Draft",
  },
  /** Omits the version chip (a first draft has no version yet). */
  hideVersionBadge: {
    type: Boolean,
    default: false,
  },
  /** Overrides the lifecycle badge text (e.g. "Proposed Change" for a pay range version). */
  statusLabel: {
    type: String,
    default: "",
  },
  /** Lead-in of the effective-date line. */
  effectivePrefix: {
    type: String,
    default: "Pay plan effective from",
  },
  /** Noun used in the chatter / actions aria-labels. */
  entityLabel: {
    type: String,
    default: "Pay plan",
  },
  /** Open-ended rows read "through —" with an "Open-ended" tooltip on the dash. */
  showOpenEndedDash: {
    type: Boolean,
    default: false,
  },
  /** Voided rows: hide "through …" when false. */
  showEndDate: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["select", "hover", "open-chatter", "menu-action"]);

const rowElement = ref(null);
const isHovered = ref(false);
const isFocused = ref(false);

const lifecycleStatus = computed(() => normalizePayPlanLifecycleStatus(props.status));

const periodState = computed(() => {
  if (props.state) return normalizePayPlanPeriodState(props.state);
  return resolvePayPlanPeriodState(
    props.effectiveFrom,
    props.openEnded ? null : props.effectiveTo,
    props.referenceDate
  );
});

const isVoided = computed(() => payPlanRowIsVoided(lifecycleStatus.value));

const resolvedVersionCode = computed(() => {
  if (props.versionCode) return props.versionCode;
  return formatPayPlanVersionCode({
    planNumber: props.planNumber,
    versionNumber: props.versionNumber,
    status: lifecycleStatus.value,
    targetsExistingPlan: props.targetsExistingPlan,
  });
});

const resolvedAmount = computed(() =>
  formatPayPlanAmount(props.amount, props.currencyCode, props.amountFormatted)
);

const effectiveText = computed(() => {
  const closedEnded =
    !props.openEnded && props.effectiveTo != null && props.effectiveTo !== "";
  if (isVoided.value && !props.showEndDate) {
    return formatPayPlanEffectiveText(props.effectiveFrom, null, true, props.effectivePrefix);
  }
  if (closedEnded) {
    return formatPayPlanEffectiveText(
      props.effectiveFrom,
      props.effectiveTo,
      false,
      props.effectivePrefix
    );
  }
  return formatPayPlanEffectiveText(props.effectiveFrom, null, true, props.effectivePrefix);
});

const openEndedDashVisible = computed(() => {
  if (!props.showOpenEndedDash || !props.effectiveFrom) return false;
  if (isVoided.value && !props.showEndDate) return false;
  const closedEnded =
    !props.openEnded && props.effectiveTo != null && props.effectiveTo !== "";
  return !closedEnded;
});

const showStateBadge = computed(() =>
  payPlanRowShowsStateBadge(lifecycleStatus.value, periodState.value)
);

const stateBadgeFilled = computed(() => payPlanRowUsesFilledStateBadge(lifecycleStatus.value));

const showMenu = computed(() => {
  const items = resolvedMenuItems.value;
  return payPlanRowShowsMenu(lifecycleStatus.value) && items.length > 0;
});

const resolvedMenuItems = computed(() => {
  if (Array.isArray(props.menuItems)) return props.menuItems;
  return buildPayPlanRowMenuItems(lifecycleStatus.value, props.approvalStatus);
});

const chatterIsWhite = computed(() => false);

const isSelected = computed(
  () => props.selected || props.interaction === "selected"
);

const showHoverRing = computed(
  () => props.interaction === "hover" || (isHovered.value && props.interaction === "default")
);

const showFocusRing = computed(
  () => props.interaction === "focus" || (isFocused.value && props.interaction === "default")
);

const rowClasses = computed(() => {
  const classes = [
    payPlanRowSurfaceClass(lifecycleStatus.value, periodState.value),
  ];
  if (payPlanRowIsCompact(lifecycleStatus.value)) {
    classes.push("pay-plan-row-card--compact");
  }
  if (isSelected.value) classes.push("pay-plan-row-card--selected");
  if (showHoverRing.value) classes.push("pay-plan-row-card--hover");
  if (showFocusRing.value) classes.push("pay-plan-row-card--focus");
  return classes;
});

const ariaLabel = computed(() => {
  const stateKey = payPlanRowPeriodBarState(lifecycleStatus.value, periodState.value);
  const openSuffix = props.openEnded ? ", open-ended" : "";
  return `${resolvedAmount.value}, ${effectiveText.value}, ${lifecycleStatus.value}, ${stateKey}${openSuffix}`;
});

function onMouseEnter(event) {
  isHovered.value = true;
  emit("hover", { hovered: true, event });
}

function onMouseLeave(event) {
  isHovered.value = false;
  emit("hover", { hovered: false, event });
}

function handleRowClick(event) {
  emit("select", event);
}

function handleRowActivate(event) {
  emit("select", event);
}

function onMenuAction(item) {
  emit("menu-action", item);
}

defineExpose({ rowElement, periodState, resolvedVersionCode, effectiveText });
</script>

<style scoped>
.pay-plan-row-card {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: none;
  padding: 18px 14px 18px 16px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  cursor: pointer;
  outline: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.pay-plan-row-card--compact {
  padding-top: 10px;
  padding-bottom: 10px;
}

.pay-plan-row-card--proposed {
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  border-radius: 10px;
  color: var(--colors-text-text-primary-900, #101828);
}

.pay-plan-row-card--in-force-future {
  background: #eff8ff;
  border: 1px solid #b2ddff;
  color: #101828;
}

.pay-plan-row-card--in-force-current {
  background: #dcfae6;
  border: 1px solid #75e0a7;
  color: #101828;
}

.pay-plan-row-card--in-force-lapsed {
  background: #f2f4f7;
  border: 1px solid #d0d5dd;
  color: #101828;
}

.pay-plan-row-card--terminated-current {
  background: #dcfae6;
  border: 1px solid #75e0a7;
  color: #101828;
}

.pay-plan-row-card--terminated-lapsed {
  background: #f2f4f7;
  border: 1px solid #d0d5dd;
  color: #101828;
}

.pay-plan-row-card--voided {
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  color: #101828;
}

.pay-plan-row-card--hover {
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
}

.pay-plan-row-card--selected {
  border-width: 2px;
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px #7f56d9;
}

.pay-plan-row-card--focus {
  border-width: 2px;
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px #7f56d9;
}

.pay-plan-row-card--proposed.pay-plan-row-card--focus {
  border-style: dashed;
}

.pay-plan-row-card__summary {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 16px;
  min-width: 0;
  overflow: hidden;
}

.pay-plan-row-card__amount {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  white-space: nowrap;
}

.pay-plan-row-card--proposed :deep(.version-badge) {
  border-style: dashed;
  border-color: var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  background: #ffffff;
  color: var(--colors-text-text-primary-900, #101828);
}

.pay-plan-row-card__effective {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.pay-plan-row-card__open-end-dash {
  cursor: help;
}

.pay-plan-row-card--proposed .pay-plan-row-card__effective {
  color: var(--colors-text-text-primary-900, #101828);
}

.pay-plan-row-card__struck {
  text-decoration: line-through;
}

.pay-plan-row-card__badges {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
}

.pay-plan-row-card__actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}

.pay-plan-row-card__chatter-btn,
.pay-plan-row-card__menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #000000;
  cursor: pointer;
  flex-shrink: 0;
}

.pay-plan-row-card__menu-btn:hover,
.pay-plan-row-card__chatter-btn:hover {
  box-shadow: 0 4px 2px rgba(0, 0, 0, 0.05);
}

.pay-plan-row-card__menu-icon {
  display: block;
  width: 4px;
  height: 18px;
  object-fit: contain;
}

.pay-plan-row-card--proposed .pay-plan-row-card__menu-btn {
  color: var(--colors-text-text-primary-900, #101828);
}

.pay-plan-row-card__chatter-btn:focus-visible,
.pay-plan-row-card__menu-btn:focus-visible,
.pay-plan-row-card:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
</style>
